import Papa from 'papaparse'
import { z } from 'zod'
import { apiRequest, ApiError } from './client'

// ── Wire schemas (camelCase, matching the FastAPI response) ──────────────────

export const HistoryRowResultSchema = z.object({
  rowIndex: z.number().int(),
  employeeNo: z.string(),
  csvEmployeeName: z.string(),
  matchedEmployeeName: z.string().nullable(),
  matchedEmployeeEmail: z.string().nullable(),
  leaveType: z.string().nullable(),
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
  days: z.string().nullable(),
  leaveStatus: z.string().nullable(),
  action: z.enum(['create', 'skip', 'error']),
  nameMismatch: z.boolean(),
  error: z.string().nullable(),
  note: z.string().nullable(),
})
export type HistoryRowResult = z.infer<typeof HistoryRowResultSchema>

export const BucketUpdateSchema = z.object({
  employeeName: z.string(),
  leaveType: z.string(),
  year: z.number().int(),
  balanceFound: z.boolean(),
  oldUsed: z.string(),
  newUsed: z.string(),
  oldPending: z.string(),
  newPending: z.string(),
  oldAvailable: z.string(),
  newAvailable: z.string(),
})
export type BucketUpdate = z.infer<typeof BucketUpdateSchema>

export const HistoryImportResultSchema = z.object({
  dryRun: z.boolean(),
  total: z.number().int(),
  wouldCreate: z.number().int(),
  skipped: z.number().int(),
  errorCount: z.number().int(),
  rows: z.array(HistoryRowResultSchema),
  bucketUpdates: z.array(BucketUpdateSchema),
})
export type HistoryImportResult = z.infer<typeof HistoryImportResultSchema>

// A row as posted to the API (strings preserve the raw export values; the
// server parses day-first dates and maps types/statuses).
export interface HistoryImportRow {
  startDate: string
  endDate: string
  employeeNo: string
  employeeName: string
  leaveType: string
  leavePeriod: string
  days: string
  leaveStatus: string
  requestId: string
}

// ── CSV parsing (old-system leave-history export header) ─────────────────────

const REQUIRED_HEADERS = [
  'start date',
  'end date',
  'employee no.',
  'leave type',
  'leave period',
  'day(s)',
  'status',
] as const

export function parseHistoryCsv(csv: string): {
  rows: HistoryImportRow[]
  errors: Array<{ line: number; message: string }>
} {
  const parsed = Papa.parse<Record<string, string>>(csv, {
    header: true,
    skipEmptyLines: true,
    transformHeader: (h) => h.trim().toLowerCase(),
  })
  const errors: Array<{ line: number; message: string }> = []
  const fields: string[] = Array.isArray(parsed.meta.fields) ? parsed.meta.fields : []
  const present = new Set(fields)
  const missing = REQUIRED_HEADERS.filter((h) => !present.has(h))
  if (missing.length > 0) {
    return {
      rows: [],
      errors: [{ line: 0, message: `Missing required column(s): ${missing.join(', ')}` }],
    }
  }

  const rows: HistoryImportRow[] = []
  parsed.data.forEach((raw, i) => {
    const line = i + 2 // header is line 1
    const startDate = (raw['start date'] ?? '').trim()
    const employeeNo = (raw['employee no.'] ?? '').trim()
    if (!startDate) {
      errors.push({ line, message: `Row ${line}: Start Date is blank` })
      return
    }
    if (!employeeNo) {
      errors.push({ line, message: `Row ${line}: Employee No. is blank` })
      return
    }
    rows.push({
      startDate,
      endDate: (raw['end date'] ?? startDate).trim() || startDate,
      employeeNo,
      employeeName: (raw['employee name'] ?? '').trim(),
      leaveType: (raw['leave type'] ?? '').trim(),
      leavePeriod: (raw['leave period'] ?? 'Full Day').trim() || 'Full Day',
      days: (raw['day(s)'] ?? '0').trim(),
      leaveStatus: (raw['status'] ?? '').trim(),
      requestId: (raw['request id'] ?? '').trim(),
    })
  })
  return { rows, errors }
}

export async function importLeaveHistory(
  rows: HistoryImportRow[],
  dryRun: boolean,
): Promise<HistoryImportResult> {
  const raw = await apiRequest<unknown>('/api/v1/hr/leave-history/import', {
    method: 'POST',
    body: { rows, dryRun },
  })
  return HistoryImportResultSchema.parse(raw)
}

// ── Export (round-trips back into the importer / legacy HRIS) ────────────────

const API_BASE = (import.meta.env.VITE_API_URL ?? '').replace(/\/+$/, '')

export interface LeaveHistoryExportFilters {
  from: string // ISO yyyy-mm-dd
  to: string // ISO yyyy-mm-dd
  status?: string // LMS status enum value; omit / 'all' for no filter
  employeeNo?: string
  // Optional extra columns. Append after the fixed round-trip columns, so the
  // file still re-imports unchanged.
  includeEmail?: boolean
  includeEmployeeReason?: boolean
  includeApproverReason?: boolean
  includeOverCapacity?: boolean
}

/**
 * Download the leave-history CSV (or a zip of CSVs when the result exceeds the
 * importer's 5000-rows-per-file cap) for the given window. Streams the file to
 * the browser via a blob + temporary anchor; throws ApiError on a non-2xx so
 * the UI can show a message instead of saving an error page.
 */
export async function exportLeaveHistory(filters: LeaveHistoryExportFilters): Promise<void> {
  const params = new URLSearchParams({ from: filters.from, to: filters.to })
  if (filters.status && filters.status !== 'all') params.set('status', filters.status)
  if (filters.employeeNo?.trim()) params.set('employeeNo', filters.employeeNo.trim())
  if (filters.includeEmail) params.set('includeEmail', 'true')
  if (filters.includeEmployeeReason) params.set('includeEmployeeReason', 'true')
  if (filters.includeApproverReason) params.set('includeApproverReason', 'true')
  if (filters.includeOverCapacity) params.set('includeOverCapacity', 'true')

  const res = await fetch(`${API_BASE}/api/v1/hr/leave-history/export?${params}`, {
    credentials: 'include',
  })
  if (!res.ok) {
    let message = `Export failed (${res.status})`
    try {
      const body = (await res.json()) as { detail?: string }
      if (body.detail) message = body.detail
    } catch {
      // non-JSON error body — keep the status-based message
    }
    throw new ApiError(message, res.status)
  }

  const blob = await res.blob()
  const filename =
    parseFilename(res.headers.get('Content-Disposition')) ??
    `lms-leave-history-${filters.from}-${filters.to}.csv`
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

function parseFilename(disposition: string | null): string | null {
  if (!disposition) return null
  const m = /filename="?([^"]+)"?/.exec(disposition)
  return m?.[1] ?? null
}

// ── Downloadable template ────────────────────────────────────────────────────

export const LEAVE_HISTORY_TEMPLATE_CSV = [
  'Row #,Start Date,End Date,Employee No.,Employee Name,Leave Type,Leave Period,Day(s),Status,Request ID',
  '1,29/5/2026,29/5/2026,03730,JANE DOE,ANNUAL LEAVE,Full Day,1.00,Approved,391772',
  '2,27/4/2026,27/4/2026,03730,JANE DOE,ANNUAL LEAVE,1st Half Day,0.50,Approved,390502',
].join('\n')
